import { useCallback, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";

import type { GameType, LocationType } from "../../types";
import Card from "./card";

interface CardsProps {
  currentLocation: LocationType | null;
  games: GameType[];
  onLocation(location: LocationType): void;
  onView(): void;
}

export default function Cards({
  currentLocation,
  games,
  onLocation,
  onView,
}: CardsProps) {
  const lastLocation = useRef<LocationType | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isProgrammaticScroll = useRef(false);

  const scrollToCard = useCallback((index: number) => {
    isProgrammaticScroll.current = true;
    cardRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    const controller = new AbortController();
    scrollContainerRef.current?.addEventListener(
      "scrollend",
      () => {
        isProgrammaticScroll.current = false;
        clearTimeout(fallbackTimeout);
      },
      { once: true, signal: controller.signal }
    );
    // Fallback: remove the scrollend listener if it never fires
    // (e.g., element already in view, no scroll needed)
    const fallbackTimeout = setTimeout(() => {
      isProgrammaticScroll.current = false;
      controller.abort();
    }, 1500);
  }, []);

  useEffect(() => {
    if (
      currentLocation &&
      (!lastLocation.current || lastLocation.current.id !== currentLocation.id)
    ) {
      const nextIndex = games.findIndex(
        (game) => game.location.id === currentLocation.id
      );

      lastLocation.current = currentLocation;

      if (nextIndex !== -1) {
        scrollToCard(nextIndex);
      }
    } else if (!lastLocation.current) {
      scrollToCard(0);
    }
  }, [currentLocation, games, scrollToCard]);

  useEffect(() => {
    if (!scrollContainerRef.current || games.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) {
          return;
        }

        let visibleIndex = -1;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleIndex = Number.parseInt(
              entry.target.getAttribute("data-index") || "-1",
              10
            );
          }
        }

        if (visibleIndex >= 0 && visibleIndex < games.length) {
          lastLocation.current = games[visibleIndex].location;
          onLocation(lastLocation.current);
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 1,
      }
    );

    for (const ref of cardRefs.current) {
      if (ref) {
        observer.observe(ref);
      }
    }

    return () => observer.disconnect();
  }, [games, onLocation]);

  return (
    <div className="absolute bottom-3 z-1 w-full md:hidden">
      <Button
        className="absolute -top-[35px] left-1/2 h-7 -translate-x-1/2 rounded-[14px] bg-white px-3.5 text-12 text-byzantium"
        onClick={onView}
      >
        List
      </Button>
      <div
        className="scrollbar-hidden flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto px-7"
        ref={scrollContainerRef}
      >
        {games.map((game, index) => (
          <Card
            game={game}
            key={game.id}
            ref={(el) => {
              cardRefs.current[index] = el || null;
              el?.setAttribute("data-index", String(index));
            }}
          />
        ))}
      </div>
    </div>
  );
}
